import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Trophy } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/news", label: "News" },
  { to: "/winners", label: "Winners" },
  { to: "/stats", label: "Stats" },
  { to: "/polls", label: "Polls" },
  { to: "/season", label: "Season 6" },
  { to: "/teams", label: "Teams" },
  { to: "/players", label: "Players" },
  { to: "/register", label: "Register" },
  { to: "/live", label: "Live" },
  { to: "/auction", label: "Auction" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/70 saturate-150 border-b border-white/60">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <img src="/asl-logo.png" alt="All Star League crest" className="h-10 w-auto transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3" />
          <span className="font-display text-xl tracking-wide text-league-ink leading-none whitespace-nowrap">
            ALL STAR LEAGUE <span className="text-league-blue">6.0</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="nav-desktop">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 text-[13px] font-semibold uppercase tracking-wider rounded-full transition-colors duration-200 whitespace-nowrap ${
                  isActive ? "bg-league-blue text-white" : "text-league-ink hover:text-league-blue"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/admin"
            data-testid="nav-link-admin"
            className="ml-2 px-4 py-2 text-sm font-bold uppercase tracking-wider rounded-full border-2 border-league-blue text-league-blue transition-colors duration-200 hover:bg-league-blue hover:text-white"
          >
            Admin
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-league-ink p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-black/5"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-4 py-3 text-sm font-bold uppercase tracking-wider rounded-lg transition-colors duration-200 ${
                      isActive ? "bg-league-blue text-white" : "text-league-ink hover:bg-league-mist"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <NavLink
                to="/admin"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-link-admin"
                className="px-4 py-3 text-sm font-bold uppercase tracking-wider rounded-lg text-league-blue"
              >
                Admin
              </NavLink>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
